"use client";

import { Truck } from "lucide-react";
import { useEffect, useState, useSyncExternalStore } from "react";
import { useCart } from "@/hooks/useCart";
import { readLastShippingPostcode, SHIPPING_POSTCODE_EVENT } from "@/lib/commerce/shippingCalculator";
import type { ProductCartFreeShipping as Result } from "@/lib/commerce/productCartShipping";

// Barra de frete grátis na página do produto (2026-10-25): para quem já tem
// produtos no carrinho, mostra se o pedido (carrinho + este produto) tem frete
// grátis para o CEP já informado no site. Mesmas regras do checkout, no
// servidor; produto fora da campanha => nada aparece.

const brl = (minor: number) => new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(minor / 100);

function subscribe(callback: () => void) {
  window.addEventListener(SHIPPING_POSTCODE_EVENT, callback);
  window.addEventListener("storage", callback);
  return () => {
    window.removeEventListener(SHIPPING_POSTCODE_EVENT, callback);
    window.removeEventListener("storage", callback);
  };
}

function readPostcode(): string {
  try {
    return readLastShippingPostcode(window.localStorage);
  } catch {
    return "";
  }
}

export function ProductCartFreeShipping({ storefrontId, quantity }: { storefrontId: number; quantity: number }) {
  const { cart } = useCart();
  const postcode = useSyncExternalStore(subscribe, readPostcode, () => "");
  const [result, setResult] = useState<{ key: string; value: Result } | null>(null);
  const signature = (cart?.items ?? []).map((item) => `${item.key}:${item.quantity}`).join("|");
  const key = `${storefrontId}:${quantity}:${postcode}:${signature}`;

  useEffect(() => {
    if (!signature || !postcode) return;
    const controller = new AbortController();
    const timer = window.setTimeout(() => {
      const query = new URLSearchParams({ cep: postcode, produto: String(storefrontId), qtd: String(quantity) });
      void fetch(`/api/cart/native/free-shipping/product?${query}`, { credentials: "same-origin", cache: "no-store", signal: controller.signal })
        .then((response) => (response.ok ? response.json() : null))
        .then((body: { result?: Result } | null) => setResult({ key, value: body?.result ?? null }))
        .catch(() => undefined);
    }, 300);
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [key, signature, postcode, storefrontId, quantity]);

  const value = signature && postcode && result?.key === key ? result.value : null;
  if (!value) return null;

  if (value.state === "missing") {
    const progress = Math.min(100, Math.round(((value.minimumMinor - value.missingMinor) / value.minimumMinor) * 100));
    return (
      <div className="mt-3 rounded-xl border border-slate-200 bg-white p-3" aria-live="polite">
        <p className="text-sm text-slate-700">
          Com este produto, faltam <strong className="text-heading">{brl(value.missingMinor)}</strong> para frete grátis para {value.place}
        </p>
        <div className="mt-2 flex items-center gap-1.5">
          <div className="h-1.5 flex-1 rounded-full bg-slate-200" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={progress} aria-label="Progresso até o frete grátis">
            <div className="h-full rounded-full bg-secondary" style={{ width: `${progress}%` }} />
          </div>
          <Truck className="h-4 w-4 shrink-0 text-slate-400" aria-hidden="true" />
        </div>
      </div>
    );
  }

  return (
    <div className="mt-3 flex items-start gap-2 rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900" aria-live="polite">
      <Truck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" aria-hidden="true" />
      <p>
        {value.state === "joins" ? (
          <>🎉 <strong>Frete grátis para {value.place}:</strong> seu carrinho já tem frete grátis e este produto também entra.</>
        ) : (
          <>🎉 <strong>Com este produto, seu pedido ganha frete grátis</strong> para {value.place}.</>
        )}
      </p>
    </div>
  );
}
